import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  usePvpRaids,
  useMyFleet,
  useMyMissions,
  useWorldMap,
  type MapIslandDto,
} from '@/api/hooks'
import { islandIdFromParam, typeFromParam } from '@/lib/questHints'
import { groupShipsByType } from '@/lib/fleetGroups'
import { isReturningMission } from '@/lib/inProgress'
import { SeaScene } from '@/components/game/SeaScene'
import { MapViewport } from '@/components/game/map/MapViewport'
import { IslandDetailSheet } from '@/components/game/map/IslandDetailSheet'
import { SheetResourceBanner } from '@/components/game/map/SheetResourceBanner'
import { MissionDetail, type FleetDetailView } from '@/components/game/map/MissionDetail'
import { useMapFocus } from '@/components/game/map/useMapFocus'
import { useMapTypeFilter } from '@/components/game/map/useMapTypeFilter'
import { useIsDesktop } from '@/hooks/useIsDesktop'
import {
  MapIcon, AnchorIcon, CombatIcon, ShipIcon,
} from '@/components/game/GameIcons'

import { toggleShipSelection } from '@/components/game/map/fleetSelection'
import {
  ISLAND_META,
  islandLegendKey,
  LEGEND_ENTRIES,
  islandMarkerMeta,
  islandTypeLabel,
  IslandFactionBadge,
  FactionBadge,
  MISSION_COLOR,
  RECIT_COLOR,
  type MarkerMeta,
} from '@/components/game/map/mapMarkers'
import {
  RationsLabel,
  PanelSection,
  ShipPickerList,
  CargoInput,
  ThreatReadout,
  IntelStalenessMarker,
  AmbushThreatCard,
  VoyageRiskNote,
  ReputBreakdown,
} from '@/components/game/map/launcherBits'
import { tripDurationSeconds, TripDuration } from '@/components/game/map/tripDuration'
import { TradeSection, TradeRunLauncher, BuyGoodsLauncher } from '@/components/game/map/launchers/TradeLaunchers'
import { RaidLauncher } from '@/components/game/map/launchers/RaidLauncher'
import { SalvageLauncher } from '@/components/game/map/launchers/SalvageLauncher'
import { ScoutLauncher } from '@/components/game/map/launchers/ScoutLauncher'
import { PvpRaidLauncher } from '@/components/game/map/launchers/PvpRaidLauncher'
import { OutlawSection, BlackMarketLauncher, SmuggleLauncher } from '@/components/game/map/launchers/OutlawLaunchers'
import {
  ExploreLauncher,
  MysteriousExpeditionPanel,
  ExpeditionDifficulty,
  ExpeditionStatus,
  ExpeditionLauncher,
} from '@/components/game/map/launchers/ExpeditionLauncher'
import { attackColor, cardinalDirection, IncomingRaidLayer, IncomingPvpRaidLayer } from '@/components/game/map/attackLayers'
import { MapRoutes } from '@/components/game/map/MapRoutes'
import { RaidLog, RaidLogColumn } from '@/components/game/map/RaidLog'
import { MapLegend } from '@/components/game/map/MapLegend'
import { SidePanel } from '@/components/game/map/SidePanel'
import { KrakenPanel } from '@/components/game/kraken/KrakenPanel'

// Ré-export barrel — MapViewport et les tests existants importent ces symboles
// depuis '@/pages/Map' / './Map' ; leur définition vit désormais dans les
// fichiers focalisés ci-dessus, mais la surface publique de ce module reste
// identique (stratégie barrel-first du découpage de Map.tsx).
export { toggleShipSelection }
export { ISLAND_META, islandLegendKey, LEGEND_ENTRIES, islandMarkerMeta, islandTypeLabel, IslandFactionBadge, FactionBadge, MISSION_COLOR, RECIT_COLOR }
export type { MarkerMeta }
export { RationsLabel, PanelSection, ShipPickerList, CargoInput, ThreatReadout, IntelStalenessMarker, AmbushThreatCard, VoyageRiskNote, ReputBreakdown }
export { tripDurationSeconds, TripDuration }
export { TradeSection, TradeRunLauncher, BuyGoodsLauncher }
export { RaidLauncher }
export { SalvageLauncher }
export { ScoutLauncher }
export { PvpRaidLauncher }
export { OutlawSection, BlackMarketLauncher, SmuggleLauncher }
export { ExploreLauncher, MysteriousExpeditionPanel, ExpeditionDifficulty, ExpeditionStatus, ExpeditionLauncher }
export { attackColor, cardinalDirection, IncomingRaidLayer, IncomingPvpRaidLayer }
export { MapRoutes }
export { RaidLog, RaidLogColumn }
export { MapLegend }
export { SidePanel }

export function Map() {
  const { data, isLoading, isError, error } = useWorldMap()
  const { data: missionsData } = useMyMissions()
  const { data: pvpRaids } = usePvpRaids()
  const { data: fleet } = useMyFleet()
  const [selected, setSelected] = useState<MapIslandDto | null>(null)
  // La bête n'est pas une île : sa sélection vit à côté, et les deux s'excluent.
  // C'est le choix le moins cher (fiche 32, lot 32d) : élargir `selected` en union
  // discriminée aurait contaminé la carte, le zoom, le focus et le deep-link.
  const [huntSelected, setHuntSelected] = useState(false)
  const isDesktop = useIsDesktop()
  const { hoveredKey, setHoveredKey } = useMapFocus()
  const typeFilter = useMapTypeFilter()
  const [selectedFleetKey, setSelectedFleetKey] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [focus, setFocus] = useState<{ islandId: number; nonce: number } | null>(null)
  // Deep-link Taverne → Carte : ?island=<id> ouvre directement l'île (et le bon
  // launcher) ; ?type=<key> pré-filtre la légende (ex. indice « story » → îles
  // mystérieuses). On consomme les deux params (replace) pour qu'un refresh/back
  // ne les rejoue pas.
  //
  // ⚠️ Anti-boucle : on dépend de `typeFilter.set` (STABLE, useCallback) et NON de
  // l'objet `typeFilter` (recréé par useMemo à chaque changement de `active`) —
  // sinon l'appel `set()` ci-dessous change `active`, recrée `typeFilter`, relance
  // l'effet, qui rappelle `set()`… → boucle infinie (heap OOM, tout le SPA figé).
  // Et on construit un URLSearchParams NEUF (jamais muter l'objet mémoïsé de
  // react-router, sous peine de désync de navigation).
  const setTypeFilter = typeFilter.set
  useEffect(() => {
    const id = islandIdFromParam(searchParams.get('island'))
    const type = typeFromParam(searchParams.get('type'))
    if (id == null && type == null) return
    // Attendre les données monde avant de résoudre un ?island, sinon l'île ciblée
    // n'est pas encore chargée et le param serait consommé sans sélection.
    if (id != null && !data) return
    if (id != null && data) {
      const island = data.islands.find((i) => i.id === id)
      if (island) {
        setSelected(island)
        setSelectedFleetKey(null)
        setFocus((f) => ({ islandId: id, nonce: (f?.nonce ?? 0) + 1 }))
      }
    }
    if (type != null) setTypeFilter([type])
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('island')
      next.delete('type')
      return next
    }, { replace: true })
  }, [data, searchParams, setSearchParams, setTypeFilter])

  if (isLoading) return <div className="max-w-6xl mx-auto p-6 text-ocean-100">Chargement de la carte…</div>
  if (isError) return <div className="max-w-6xl mx-auto p-6 text-rum-500">Erreur : {(error as Error).message}</div>
  if (!data) return null

  const discoveredCount = data.islands.filter((i) => i.isDiscovered).length
  // Outbound AND returning (cancelled, not yet landed) missions both keep a
  // fleet visibly at sea — the caption/count must not drop a recalled fleet
  // the instant it's cancelled (it's still sailing, just homebound).
  const inProgress = missionsData?.missions.filter((m) => m.status === 'in_progress' || isReturningMission(m)) ?? []
  const lead = inProgress[0]
  const dockedGroups = groupShipsByType(fleet?.ships.filter((s) => s.status === 'docked') ?? [])
  const myIsland = data.islands.find((i) => i.isMine) ?? null

  // Vue détaillée de la flotte sélectionnée (clé `m-<id>` mission / `pvp-<id>` raid).
  const fleetView: FleetDetailView | null = (() => {
    if (!selectedFleetKey) return null
    if (selectedFleetKey.startsWith('m-')) {
      const id = Number(selectedFleetKey.slice(2))
      const m = missionsData?.missions.find((x) => x.id === id)
      if (!m) return null
      const returning = isReturningMission(m)
      return {
        typeLabel: returning ? `Retour · ${m.typeLabel}` : m.typeLabel,
        color: MISSION_COLOR[m.type] ?? '#5FBDD0',
        // Homebound: the fleet detail should read "→ mon île", not the
        // (already-visited) mission destination — resolvesAt is still the
        // right ETA, it's now the return-arrival time.
        destName: returning ? myIsland?.name ?? '—' : m.destinationIslandName ?? '—',
        resolvesAt: m.resolvesAt,
        fleet: m.fleet ?? [],
        mission: m,
      }
    }
    const id = Number(selectedFleetKey.slice(4))
    const r = pvpRaids?.sent.find((x) => x.id === id)
    if (!r || !r.scheduledAt) return null
    return {
      typeLabel: 'Raid',
      color: MISSION_COLOR.raid_pvp,
      destName: r.defenderIslandName ?? '—',
      resolvesAt: r.scheduledAt,
      fleet: r.fleet ?? [],
    }
  })()

  // Exclusivité : sélectionner une île vide la flotte, et réciproquement.
  const selectIsland = (i: MapIslandDto | null) => {
    setSelected(i)
    setSelectedFleetKey(null)
    setHuntSelected(false)
  }
  const selectFleet = (key: string) => {
    setSelectedFleetKey(key)
    setSelected(null)
    setHuntSelected(false)
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 pb-[300px] space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-x-4 gap-y-1 flex-wrap">
          <h1 className="font-display text-3xl sm:text-4xl text-sand-300 flex items-center gap-3">
            <MapIcon className="w-8 h-8 text-sand-300" /> Carte du monde
          </h1>
          {dockedGroups.length > 0 && (
            <span className="flex items-center gap-2.5 text-ocean-200" title="Navires disponibles à quai">
              <AnchorIcon className="w-4 h-4 text-sand-300/70" />
              {dockedGroups.map((g) => (
                <span
                  key={g.type}
                  className="inline-flex items-center gap-1 tabular-nums"
                  title={`${g.ships.length} ${g.label} à quai`}
                >
                  {g.ships.length}
                  <ShipIcon type={g.type} className="w-5 h-5 text-sand-300" />
                </span>
              ))}
            </span>
          )}
        </div>
        <span className="text-sm text-ocean-300">
          {data.season.name} · cartographiées <strong className="text-sand-300">{discoveredCount}</strong>/{data.islands.length}
        </span>
      </div>

      <div className={isDesktop ? 'grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4' : 'space-y-4'}>
        <MapViewport
          data={data}
          selected={selected}
          onSelect={selectIsland}
          huntSelected={huntSelected}
          onSelectHunt={() => {
            setSelected(null)
            setSelectedFleetKey(null)
            setHuntSelected(true)
          }}
          hoveredKey={hoveredKey}
          setHoveredKey={setHoveredKey}
          onSelectFleet={selectFleet}
          filter={typeFilter}
          focusIslandId={focus?.islandId ?? null}
          focusNonce={focus?.nonce ?? 0}
        />
        {isDesktop &&
          (fleetView ? (
            <MissionDetail view={fleetView} onClose={() => setSelectedFleetKey(null)} />
          ) : huntSelected ? (
            <KrakenPanel onCleared={() => setHuntSelected(false)} />
          ) : (
            <SidePanel
              selected={selected}
              onCleared={() => selectIsland(null)}
              incomingAttack={data.incomingAttack}
              incomingPlayerRaids={data.incomingPlayerRaids}
            />
          ))}
      </div>

      {/* Mobile (<lg) : la sélection lève une feuille basse — détail de mission ou d'île.
          La feuille recouvre le header (et son bandeau ressources) : on épingle donc
          la banque du joueur en tête de feuille, sinon impossible de jauger un coût
          de mission (rhum, or, cale) sans refermer. */}
      {!isDesktop && (
        <IslandDetailSheet
          open={selected !== null || fleetView !== null || huntSelected}
          onClose={() => {
            setSelected(null)
            setSelectedFleetKey(null)
            setHuntSelected(false)
          }}
          header={fleet?.resources ? <SheetResourceBanner resources={fleet.resources} /> : undefined}
        >
          {fleetView ? (
            <MissionDetail view={fleetView} onClose={() => setSelectedFleetKey(null)} />
          ) : huntSelected ? (
            <KrakenPanel variant="sheet" onCleared={() => setHuntSelected(false)} />
          ) : (
            selected && (
              <SidePanel
                variant="sheet"
                selected={selected}
                onCleared={() => selectIsland(null)}
                incomingAttack={data.incomingAttack}
                incomingPlayerRaids={data.incomingPlayerRaids}
              />
            )
          )}
        </IslandDetailSheet>
      )}

      <MapLegend filter={typeFilter} />

      <RaidLog />

      {/* Integrated sea — a fleet sailing while missions are out (incl. a
          recalled fleet still homebound after a cancel). */}
      {lead && (
        <SeaScene
          scene="map"
          caption={{
            icon: <CombatIcon />,
            label: isReturningMission(lead) ? (
              <><strong className="text-sand-300">{inProgress.length} mission{inProgress.length > 1 ? 's' : ''} en cours</strong>{lead.shipName ? ` (${lead.shipName})` : ''} <span aria-hidden="true">↩</span> retour au port</>
            ) : (
              <><strong className="text-sand-300">{inProgress.length} mission{inProgress.length > 1 ? 's' : ''} en cours</strong>{lead.shipName ? ` (${lead.shipName})` : ''}{lead.destinationIslandName ? ` → ${lead.destinationIslandName}` : ''}</>
            ),
          }}
        />
      )}
    </div>
  )
}
